import React from 'react';
import {useQuery, useMutation, gql} from '@apollo/client';
import { Toast } from 'antd-mobile';
import {Header} from './components/Header';
import {Pda} from './components/Pda';
import {ExceptionScanComponent, ExceptionScanResult} from './components/ExceptionScanComponent';
import {
    GET_EXCEPTION_DICTIONARY_BY_WAREHOUSE_TYPE,
    ExceptionDictionaryListResponse
} from '../../graphql/queries/exceptionQueries';
import {t} from '../../i18n';
import {isTrackingNumber, showTrackingNumberError} from '../../utils/BarCodeUtil';
import {WarehouseSiteType} from '../../enums/WarehouseTypeEnum';
import { fileUpload } from '../../utils/storageUtils';
import { base64ToFile } from '../../services/DeliveryDataService';

export const HANDLE_EXCEPTION_SCAN_EVENT = gql`
    mutation HandleExceptionScanEvent(
        $trackingNumber: String!
        $warehouseSiteType: WarehouseSiteType!
        $exceptionReasonCode: String!
        $exceptionImageS3KeysJson: [String!]
    ) {
        success: handleExceptionScanEvent(
            trackingNumber: $trackingNumber
            warehouseSiteType: $warehouseSiteType
            exceptionReasonCode: $exceptionReasonCode
            exceptionImageS3KeysJson: $exceptionImageS3KeysJson
        )
    }
`;

const PageBody = () => {
    // 获取 Hub 异常字典
    const {data: queryData, loading} = useQuery<ExceptionDictionaryListResponse>(
        GET_EXCEPTION_DICTIONARY_BY_WAREHOUSE_TYPE,
        {
            variables: {
                warehouseSiteType: WarehouseSiteType.HUB
            }
        }
    );

    // 异常扫描 mutation
    const [handleExceptionScanEvent] = useMutation(HANDLE_EXCEPTION_SCAN_EVENT);

    // 处理单个扫描（不需要拍照的异常类型）
    const handleSingleScan = async (barCode: string, selectedExceptionCode: string): Promise<ExceptionScanResult> => {
        try {
            // 验证条码格式
            if (!isTrackingNumber(barCode)) {
                showTrackingNumberError(barCode);
                throw new Error(`Invalid tracking number: ${barCode}`);
            }

            // 调用后端异常扫描接口（不需要照片）
            const { data } = await handleExceptionScanEvent({
                variables: {
                    trackingNumber: barCode,
                    warehouseSiteType: WarehouseSiteType.HUB,
                    exceptionReasonCode: selectedExceptionCode,
                    exceptionImageS3KeysJson: null
                }
            });

            if (!data?.success) {
                throw new Error(`Failed to process tracking number: ${barCode}`);
            }

            return {
                success: true,
                data: {
                    trackingNumber: barCode,
                    success: data.success
                }
            };

        } catch (error) {
            throw error;
        }
    };

    // 处理提交（需要拍照的异常类型）
    const handleSubmit = async (trackingNumber: string, selectedExceptionCode: string, photos: string[]): Promise<ExceptionScanResult> => {
        try {
            // 准备照片数据 - 上传照片到S3并获取objectKeys
            let exceptionImageS3KeysJson: string[] | null = null;
            
            if (photos.length > 0) {
                try {
                    // 将base64照片上传到S3
                    const uploadPromises = photos.map(async (base64Photo) => {
                        // 确保base64格式正确
                        const formattedBase64 = base64Photo.startsWith('data:') ? base64Photo : `data:image/jpeg;base64,${base64Photo}`;
                        // 转换为File对象
                        const file = await base64ToFile(formattedBase64, `exception_${Date.now()}.jpg`);
                        // 上传到S3
                        const result = await fileUpload(file, 'exception_photo');
                        return result.objectKey;
                    });
                    
                    exceptionImageS3KeysJson = await Promise.all(uploadPromises);
                } catch (error) {
                    Toast.show({ content: t('pda.pages.exception.photoUploadFailed'), position: 'center' });
                    throw error;
                }
            }

            // 处理单个扫描的条码
            // 验证条码格式
            if (!isTrackingNumber(trackingNumber)) {
                showTrackingNumberError(trackingNumber);
                throw new Error(`Invalid tracking number: ${trackingNumber}`);
            }

            // 调用后端异常扫描接口
            const { data } = await handleExceptionScanEvent({
                variables: {
                    trackingNumber: trackingNumber,
                    warehouseSiteType: WarehouseSiteType.HUB,
                    exceptionReasonCode: selectedExceptionCode,
                    exceptionImageS3KeysJson: exceptionImageS3KeysJson
                }
            });

            if (!data?.success) {
                throw new Error(`Failed to process tracking number: ${trackingNumber}`);
            }

            return {
                success: true,
                data: {
                    trackingNumber: trackingNumber,
                    success: data.success
                }
            };

        } catch (error) {
            throw error;
        }
    };

    return (
        <div className="flex-1 overflow-hidden flex flex-col">
            {/* 异常扫描组件 */}
            <div className="flex-1 overflow-hidden">
                <ExceptionScanComponent
                    exceptionDictionary={queryData?.getExceptionsDictionaryByWarehouseType || []}
                    loading={loading}
                    onSubmit={handleSubmit}
                    onSingleScan={handleSingleScan}
                    scanPlaceholder={t('pda.pages.exception.trackingNO')}
                    scanColumnTitle={t('pda.pages.exception.trackingNO')}
                    exceptionPlaceholder={t('pda.pages.exception.selectExceptionReason')}
                    submitButtonText={t('pda.common.submit')}
                    successExtraFields={[
                        { title: t('pda.pages.exception.exceptionReason'), key: 'exceptionReason' }
                    ]}
                />
            </div>
        </div>
    );
};

export default function HubExceptionPage() {
    return (
        <div className="w-full h-full flex flex-col gap-2 p-2">
            <Header title={t('pda.pages.exception.exceptionScan')}/>
            <Pda/>
            <PageBody/>
        </div>
    );
}
